import {useEffect, useState} from "react";
import {IApiResult} from "../../../../api/AxiosWealthManagementApi";

/**
 * Module-level cache (Option A1): one Map per browser tab, alive until a hard reload.
 * Keyed by (tab, startDate, endDate, country) so a new filter set never serves stale data.
 * The container empties it on filter change (clearAnalyticsCache) to bound memory to the
 * current filter set.
 */
const cache = new Map<string, unknown>();
const inflight = new Map<string, Promise<unknown>>();

interface Range {
    startDate: string;
    endDate: string;
}

type Fetcher<T> = (startDate: string, endDate: string, country?: string) => Promise<IApiResult<T>>;

const keyOf = (tab: string, range: Range, country: string): string =>
    `${tab}|${range.startDate}|${range.endDate}|${country || "all"}`;

export function clearAnalyticsCache(): void {
    cache.clear();
    inflight.clear();
}

export interface AnalyticsTabDataState<T> {
    data: T;
    loading: boolean;
    loaded: boolean;
    error: string | null;
    reload: () => void;
}

export function useAnalyticsTabData<T>(
    tab: string,
    fetcher: Fetcher<T>,
    range: Range,
    country: string,
    fallback: T,
): AnalyticsTabDataState<T> {
    const key = keyOf(tab, range, country);
    const [data, setData] = useState<T>((cache.get(key) as T) ?? fallback);
    const [loaded, setLoaded] = useState<boolean>(cache.has(key));
    const [loading, setLoading] = useState<boolean>(!cache.has(key));
    const [error, setError] = useState<string | null>(null);
    const [reloadCount, setReloadCount] = useState<number>(0);

    useEffect(() => {
        if (cache.has(key)) {
            setData(cache.get(key) as T);
            setLoaded(true);
            setLoading(false);
            setError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        const promise = inflight.get(key) ?? fetcher(range.startDate, range.endDate, country || undefined)
            .then((result) => {
                const value = result.data ?? fallback;
                cache.set(key, value);
                inflight.delete(key);
                return value as unknown;
            });
        inflight.set(key, promise);
        promise
            .then((value) => {
                if (cancelled) return;
                setData(value as T);
                setLoaded(true);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                inflight.delete(key);
                setError("Could not load data.");
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [key, reloadCount]);

    const reload = (): void => {
        cache.delete(key);
        inflight.delete(key);
        setReloadCount((c) => c + 1);
    };

    return {data, loading, loaded, error, reload};
}
